import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
// The chat platforms Giga lives inside. These cycle in the home hero headline
// ("...your team in ___") to show Giga isn't locked to a single workspace.
// Order matters — Slack leads since it's the primary positioning. Each carries
// its real brand mark + chip color so the platform reads instantly as a logo,
// not just a word.
type Platform = {
  name: string;
  // Inline brand glyph so it always renders in full color (no CDN fallback).
  logo: React.ReactNode;
  // Brand-tinted "liquid glass" pill: a translucent base tint, a soft border,
  // and a colored glow so each platform reads as its own frosted chip.
  bg: string;
  border: string;
  glow: string;
  text: string;
};
const SlackGlyph = () => <svg viewBox="0 0 24 24" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-786">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-787" />
    <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-788" />
    <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-789" />
    <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-790" />
  </svg>;
const TeamsGlyph = () => <svg viewBox="0 0 2228.833 2073.333" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-791">
    <path fill="#5059C9" d="M1554.637 777.5h575.713c54.391 0 98.483 44.092 98.483 98.483v524.398c0 199.901-162.051 361.952-361.952 361.952h-1.711c-199.901.028-361.975-162-362.004-361.901V828.971c.001-28.427 23.045-51.471 51.471-51.471z" data-id="element-792" />
    <circle fill="#5059C9" cx="1943.75" cy="440.583" r="233.25" data-id="element-793" />
    <circle fill="#7B83EB" cx="1218.083" cy="336.917" r="336.917" data-id="element-794" />
    <path fill="#7B83EB" d="M1667.323 777.5H717.01c-53.743 1.33-96.257 45.931-95.01 99.676v598.105c-7.505 322.519 247.657 590.16 570.167 598.053 322.51-7.893 577.671-275.534 570.167-598.053V877.176c1.245-53.745-41.268-98.346-95.011-99.676z" data-id="element-795" />
    <path opacity=".1" d="M1244 777.5v838.145c-.258 38.435-23.549 72.964-59.09 87.598a91.856 91.856 0 0 1-35.967 7.257H667.613c-6.738-17.105-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52H1244z" data-id="element-796" />
    <path opacity=".2" d="M1192.167 777.5v889.978a91.802 91.802 0 0 1-7.257 35.967c-14.634 35.541-49.163 58.833-87.598 59.09H691.975c-8.812-17.105-17.105-34.21-24.362-51.833-7.257-17.623-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.313z" data-id="element-797" />
    <path opacity=".2" d="M1192.167 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.312z" data-id="element-798" />
    <path opacity=".2" d="M1140.333 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h423.478z" data-id="element-799" />
    <path opacity=".1" d="M1244 509.522v163.275c-8.812.518-17.105 1.037-25.917 1.037-8.812 0-17.105-.518-25.917-1.037a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973a288.02 288.02 0 0 1-16.587-51.833h261.265c52.305.198 94.657 42.549 94.854 94.855z" data-id="element-800" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-801" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-802" />
    <path opacity=".2" d="M1140.333 561.355v103.073a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h136.41c52.306.198 94.658 42.549 94.855 94.902z" data-id="element-803" />
    <linearGradient id="teamsg" gradientUnits="userSpaceOnUse" x1="198.099" y1="1683.0726" x2="942.2344" y2="394.2607" gradientTransform="matrix(1 0 0 -1 0 2075.3333)" data-id="element-804">
      <stop offset="0" stopColor="#5a62c3" data-id="element-805" />
      <stop offset=".5" stopColor="#4d55bd" data-id="element-806" />
      <stop offset="1" stopColor="#3940ab" data-id="element-807" />
    </linearGradient>
    <path fill="url(#teamsg)" d="M95.01 561.355h950.312c52.473 0 95.01 42.538 95.01 95.01v950.312c0 52.473-42.538 95.01-95.01 95.01H95.01c-52.473 0-95.01-42.538-95.01-95.01V656.365c0-52.472 42.538-95.01 95.01-95.01z" data-id="element-808" />
    <path fill="#fff" d="M820.211 922.038H630.241v517.297H509.211V922.038H320.123V821.456h500.088z" data-id="element-809" />
  </svg>;
const PLATFORMS: Platform[] = [{
  name: 'Slack',
  logo: <SlackGlyph data-id="element-810" />,
  // Opaque, light aubergine-tinted base — reads as a frosted glass pane but
  // is fully solid so the dotted background never shows through.
  bg: '#F0EAF2',
  border: 'rgba(74,21,75,0.22)',
  glow: 'rgba(74,21,75,0.18)',
  text: '#4A154B'
}, {
  name: 'Teams',
  logo: <TeamsGlyph data-id="element-811" />,
  // Opaque, light indigo-tinted base — same solid frosted-glass treatment.
  bg: '#EAEBF8',
  border: 'rgba(80,89,201,0.26)',
  glow: 'rgba(80,89,201,0.20)',
  text: '#4B53BC'
}];
export function RotatingPlatform() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % PLATFORMS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce]);
  return (
    // Reserve the width of the WIDEST platform chip by stacking every option
    // invisibly in the same grid cell. This keeps the total sentence width
    // constant so the preceding words ("...your team in") never reflow or
    // re-center — only the platform chip swaps. The chip is left-anchored so
    // it always starts at the same x and any extra room sits to its right.
    <span className="relative inline-grid place-items-start align-baseline not-italic" data-id="element-812">
      <span className="invisible col-start-1 row-start-1" aria-hidden="true" data-id="element-813">
        {PLATFORMS.map(p => <span key={p.name} className="block" data-id="element-814">
            <PlatformChip platform={p} data-id="element-815" />
          </span>)}
      </span>
      <span className="col-start-1 row-start-1" data-id="element-816">
        <AnimatePresence mode="wait" initial={false} data-id="element-817">
          <motion.span key={PLATFORMS[index].name} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '0.4em',
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '-0.4em',
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className="inline-block whitespace-nowrap" data-id="element-818">
            <PlatformChip platform={PLATFORMS[index]} data-id="element-819" />
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}
// A "liquid glass" pill that pairs the brand logo with the platform name, so
// the headline makes it instantly obvious Giga lives in Slack or Teams. The
// chip is a frosted, brand-tinted pane: a translucent base, a backdrop blur, a
// glossy specular sheen along the top, and a soft brand-colored glow.
function PlatformChip({
  platform
}: {
  platform: Platform;
}) {
  return <span className="relative inline-flex items-center gap-[0.3em] overflow-hidden rounded-[0.55em] px-[0.45em] py-[0.08em] align-baseline not-italic font-semibold leading-none border [backdrop-filter:blur(8px)_saturate(1.4)] [-webkit-backdrop-filter:blur(8px)_saturate(1.4)]" style={{
    backgroundColor: platform.bg,
    borderColor: platform.border,
    color: platform.text,
    // Nudge the chip down a touch so it sits a bit lower and doesn't crowd
    // the headline line above it on multi-line layouts.
    transform: 'translateY(0.12em)',
    boxShadow: `0 1px 1px rgba(255,255,255,0.5) inset, 0 -1px 1px rgba(0,0,0,0.04) inset, 0 4px 14px -4px ${platform.glow}, 0 1px 3px rgba(0,0,0,0.06)`
  }} data-id="element-820">
      {/* Glossy specular sheen across the top half — the "liquid glass" gleam */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[0.55em]" style={{
      background: 'linear-gradient(to bottom, rgba(255,255,255,0.55), rgba(255,255,255,0.08) 45%, rgba(255,255,255,0) 60%)'
    }} data-id="element-821" />
      {/* Soft highlight blob in the top-left for extra liquid depth */}
      <span aria-hidden="true" className="pointer-events-none absolute -left-[10%] -top-[40%] h-[120%] w-[55%] rounded-full" style={{
      background: 'radial-gradient(closest-side, rgba(255,255,255,0.5), rgba(255,255,255,0) 70%)'
    }} data-id="element-822" />
      <span className="relative flex items-center" data-id="element-823">{platform.logo}</span>
      <span className="relative" data-id="element-824">{platform.name}</span>
    </span>;
}